import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HandHeart, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const presets = [25, 50, 100, 250];

export const Donate = () => {
  const [selected, setSelected] = useState(50);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one_time");
  const [loading, setLoading] = useState(false);

  const amount = custom ? parseFloat(custom) : selected;

  const give = async () => {
    if (!amount || isNaN(amount) || amount < 1) {
      toast.error("Please enter an amount of $1 or more.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/donations/checkout`, {
        amount,
        origin_url: window.location.origin,
        frequency,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("We couldn't start the donation. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section id="give" className="py-24 lg:py-32 bg-[#2C2A28] relative overflow-hidden" data-testid="donate-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 relative">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C4B299] mb-4">06 — Give</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-[#FAF9F6]">
            Their gift has no price.
            <br />
            <em className="italic text-[#C4B299]">Your giving keeps it free.</em>
          </h2>
          <p className="text-[#C4B299]/90 leading-relaxed font-light mt-6 max-w-xl">
            The Wicks don't charge couples for coaching. Donations keep this ministry going
            and open the door for couples who could never afford help otherwise.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 inline-flex rounded-full border border-[#C4B299]/40 p-1" data-testid="donate-frequency-toggle">
            {[
              { key: "one_time", label: "One-time" },
              { key: "monthly", label: "Monthly" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFrequency(f.key)}
                className={`rounded-full px-7 py-2.5 text-sm transition-colors duration-300 ${
                  frequency === f.key
                    ? "bg-[#C4B299] text-[#2C2A28]"
                    : "text-[#C4B299] hover:text-[#FAF9F6]"
                }`}
                data-testid={`donate-frequency-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {presets.map((p) => (
              <button
                key={p}
                onClick={() => {
                  setSelected(p);
                  setCustom("");
                }}
                className={`rounded-full px-8 py-3.5 text-sm border transition-colors duration-300 ${
                  !custom && selected === p
                    ? "bg-[#8A5A44] border-[#8A5A44] text-[#FAF9F6]"
                    : "border-[#C4B299]/40 text-[#C4B299] hover:border-[#C4B299]"
                }`}
                data-testid={`donate-preset-${p}`}
              >
                ${p}
              </button>
            ))}
            <div className="relative">
              <span className="absolute left-5 top-1/2 -translate-y-1/2 text-[#C4B299] text-sm">$</span>
              <input
                type="number"
                min="1"
                placeholder="Custom"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                className="rounded-full bg-transparent border border-[#C4B299]/40 focus:border-[#C4B299] text-[#FAF9F6] text-sm pl-9 pr-5 py-3.5 w-36 outline-none placeholder:text-[#C4B299]/50 transition-colors duration-300"
                data-testid="donate-custom-input"
              />
            </div>
          </div>

          <button
            onClick={give}
            disabled={loading}
            className="group mt-10 rounded-full bg-[#8A5A44] hover:bg-[#734A38] disabled:opacity-60 text-[#FAF9F6] px-10 py-4 text-sm flex items-center gap-2.5 transition-colors duration-300"
            data-testid="donate-submit-button"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <HandHeart size={16} />}
            {loading
              ? "Opening secure checkout…"
              : `Give $${amount || 0}${frequency === "monthly" ? " / month" : ""}`}
          </button>
          <p className="text-xs text-[#C4B299]/60 mt-5">
            Secure checkout by Stripe. Nonprofit status in progress — receipts available on request.
          </p>

          <div className="mt-10 pt-8 border-t border-[#C4B299]/20">
            <p className="text-xs uppercase tracking-[0.25em] text-[#C4B299] mb-3">Or send directly</p>
            <a
              href="https://cash.app/$mamawicks"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full bg-[#00C244] hover:bg-[#00A038] text-white px-7 py-3.5 text-sm transition-colors duration-300"
              data-testid="donate-cashapp-link"
              aria-label="Give via Cash App to $mamawicks"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                <path d="M17.4 3H6.6A3.6 3.6 0 0 0 3 6.6v10.8A3.6 3.6 0 0 0 6.6 21h10.8a3.6 3.6 0 0 0 3.6-3.6V6.6A3.6 3.6 0 0 0 17.4 3Zm-2.03 7.4a.6.6 0 0 1-.83.03 3.2 3.2 0 0 0-2.03-.83c-.62 0-1.27.24-1.27.78 0 .56.65.75 1.4.98 1.36.42 3.14 1 3.14 2.95 0 1.66-1.24 2.75-2.86 2.98l-.14.76a.6.6 0 0 1-.6.5h-.98a.6.6 0 0 1-.59-.7l.16-.87a4.8 4.8 0 0 1-2.35-1.06.6.6 0 0 1-.03-.86l.62-.6a.6.6 0 0 1 .82-.02c.61.52 1.42.85 2.28.85.83 0 1.4-.34 1.4-.9 0-.55-.6-.75-1.5-1.05-1.26-.4-2.98-.94-2.98-2.86 0-1.7 1.32-2.7 2.83-2.9l.15-.83a.6.6 0 0 1 .6-.5h.97a.6.6 0 0 1 .58.7l-.15.85c.65.19 1.25.5 1.77.94a.6.6 0 0 1 .03.86l-.6.6Z" />
              </svg>
              Cash App · $mamawicks
            </a>
            <p className="text-xs text-[#C4B299]/60 mt-3">Fast and simple — opens in the Cash App.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
